import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CButton, CInput, CText, CHeader } from '../../components/common';
import { emailValidation } from '../../utils/validation';
import { updateUserData, fetchUserData } from '../../api/auth';
import { setUserData as saveUserData } from '../../utils/localStorage';
import { FiCamera, FiTrendingUp, FiAward } from 'react-icons/fi';

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const currentUser = useAppSelector((state) => state.user.user);
  const authToken = useAppSelector((state) => state.user.authToken);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step counter: 0 = Basic Info, 1 = Persona Selection, 2 = Expert Details
  const [step, setStep] = useState(0);

  // Basic info
  const [fullName, setFullName] = useState(
    currentUser?.name || location.state?.fullName || ''
  );
  const [email, setEmail] = useState(currentUser?.email || '');
  const [phone, setPhone] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Persona
  const [isExpert, setIsExpert] = useState(false);

  // Expert details
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [sebiQualification, setSebiQualification] = useState('');
  const [sebiRegNumber, setSebiRegNumber] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [languages, setLanguages] = useState('');
  const [callRate, setCallRate] = useState('');
  const [chatRate, setChatRate] = useState('');

  // Errors & loading
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(false);

  const sebiQualifications = [
    'Investment Advisor',
    'Research Analyst',
    'Portfolio Manager',
    'Stockbroker',
    'Merchant Banker',
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const validateStep0 = (): boolean => {
    let valid = true;
    setSaveError('');

    if (!fullName.trim()) {
      setNameError(strings.thisFieldIsMandatory);
      valid = false;
    } else {
      setNameError('');
    }

    if (!email.trim()) {
      setEmailError(strings.thisFieldIsMandatory);
      valid = false;
    } else if (!emailValidation(email)) {
      setEmailError(strings.enterValidEmail);
      valid = false;
    } else {
      setEmailError('');
    }

    return valid;
  };

  const handleNext = () => {
    if (step === 0) {
      if (!validateStep0()) return;
      setStep(1);
    } else if (step === 1) {
      if (isExpert) {
        setStep(2);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!authToken) {
      setSaveError('Session expired. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', fullName.trim());
      payload.append('email', email.trim());
      if (phone.trim()) payload.append('phone_number', phone.trim());
      if (selectedFile) payload.append('image', selectedFile);
      payload.append('is_expert', isExpert.toString());

      if (isExpert) {
        if (headline.trim()) payload.append('headline', headline.trim());
        if (bio.trim()) payload.append('bio', bio.trim());
        if (sebiQualification) payload.append('sebi_qualification', sebiQualification);
        if (sebiRegNumber.trim()) payload.append('sebi_reg_number', sebiRegNumber.trim());
        if (yearsOfExperience) payload.append('years_of_xp', yearsOfExperience);
        if (languages.trim()) {
          payload.append('languages', JSON.stringify(
            languages.split(',').map((l) => l.trim()).filter(Boolean)
          ));
        }
        if (callRate) payload.append('call_session_rate', callRate);
        if (chatRate) payload.append('chat_session_rate', chatRate);
      }

      await updateUserData(authToken, payload);

      const freshUser = await fetchUserData(authToken);
      dispatch(setUser(freshUser as any));
      saveUserData(freshUser as any);

      navigate(routes.home);
    } catch (error: any) {
      console.error('Error updating user details:', error);
      setSaveError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to update profile. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step indicator dots
  const totalSteps = isExpert ? 3 : 2;
  const StepIndicator = () => (
    <div className="flex justify-center gap-2 mb-6">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div
          key={i}
          className="h-2 rounded-full transition-all"
          style={{
            width: i === step ? '24px' : '8px',
            backgroundColor: i === step ? colors.primary : colors.grayScale500 + '40',
          }}
        />
      ))}
    </div>
  );

  // Step 0: Basic Info
  const renderBasicInfo = () => (
    <>
      <h2
        className="text-2xl md:text-3xl font-bold mb-2"
        style={{ color: colors.textColor }}
      >
        Complete Your Profile
      </h2>
      <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
        Tell us a bit about yourself
      </CText>

      {/* Profile Image */}
      <div className="flex justify-center mb-6">
        <div className="relative">
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Profile"
              className="w-20 h-20 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              {fullName.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.primary }}
            onClick={() => fileInputRef.current?.click()}
          >
            {React.createElement(FiCamera as any, { size: 14, color: colors.white })}
          </button>
        </div>
      </div>

      <CInput
        label={strings.fullName}
        value={fullName}
        onChange={setFullName}
        placeholder="Enter your full name"
        errorText={nameError}
        required
      />

      <CInput
        label={strings.email}
        value={email}
        onChange={setEmail}
        placeholder="Enter your email"
        type="email"
        errorText={emailError}
        required
      />

      <CInput
        label="Phone Number"
        value={phone}
        onChange={setPhone}
        placeholder="Enter your phone number"
        type="tel"
      />

      {saveError && (
        <CText type="r12" color="#ef4444" style={{ marginBottom: '8px' }}>
          {saveError}
        </CText>
      )}

      <div className="mt-4">
        <CButton title="Next" onPress={handleNext} />
      </div>
    </>
  );

  // Step 1: Persona Selection
  const renderPersonaSelection = () => (
    <>
      <h2
        className="text-2xl md:text-3xl font-bold mb-2"
        style={{ color: colors.textColor }}
      >
        How will you use Yantrai?
      </h2>
      <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '24px' }}>
        Choose your path — you can always change this later
      </CText>

      <div className="space-y-4">
        {/* Investor Card */}
        <button
          className="w-full p-5 rounded-xl border-2 text-left transition-all relative"
          style={{
            borderColor: !isExpert ? colors.primary : (colors.dark ? colors.grayScale700 : colors.grayScale200),
            backgroundColor: !isExpert ? colors.primary + '08' : 'transparent',
          }}
          onClick={() => setIsExpert(false)}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: colors.primary + '15' }}
            >
              {React.createElement(FiTrendingUp as any, { size: 24, color: colors.primary })}
            </div>
            <div>
              <CText type="B16" color={colors.textColor}>
                Investor
              </CText>
              <CText type="r14" color={colors.grayScale500} style={{ marginTop: '4px' }}>
                Get expert insights, track markets, and manage your portfolio
              </CText>
            </div>
          </div>
          {!isExpert && (
            <div
              className="w-5 h-5 rounded-full absolute top-4 right-4 flex items-center justify-center"
              style={{ backgroundColor: colors.primary }}
            >
              <span className="text-white text-xs">✓</span>
            </div>
          )}
        </button>

        {/* Expert Card */}
        <button
          className="w-full p-5 rounded-xl border-2 text-left transition-all relative"
          style={{
            borderColor: isExpert ? colors.primary : (colors.dark ? colors.grayScale700 : colors.grayScale200),
            backgroundColor: isExpert ? colors.primary + '08' : 'transparent',
          }}
          onClick={() => setIsExpert(true)}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#f59e0b15' }}
            >
              {React.createElement(FiAward as any, { size: 24, color: '#f59e0b' })}
            </div>
            <div>
              <CText type="B16" color={colors.textColor}>
                Financial Expert
              </CText>
              <CText type="r14" color={colors.grayScale500} style={{ marginTop: '4px' }}>
                Share your expertise, offer consultations, and earn money
              </CText>
            </div>
          </div>
          {isExpert && (
            <div
              className="w-5 h-5 rounded-full absolute top-4 right-4 flex items-center justify-center"
              style={{ backgroundColor: '#f59e0b' }}
            >
              <span className="text-white text-xs">✓</span>
            </div>
          )}
        </button>
      </div>

      {saveError && (
        <CText type="r12" color="#ef4444" style={{ marginTop: '12px' }}>
          {saveError}
        </CText>
      )}

      <div className="mt-6">
        <CButton
          title={loading && !isExpert ? 'Saving...' : (isExpert ? 'Next' : 'Get Started')}
          onPress={handleNext}
          disabled={loading}
        />
      </div>
    </>
  );

  // Step 2: Expert Details
  const renderExpertDetails = () => (
    <>
      <h2
        className="text-2xl md:text-3xl font-bold mb-2"
        style={{ color: colors.textColor }}
      >
        Expert Profile
      </h2>
      <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
        Help users know your expertise
      </CText>

      <CInput
        label="Profile Headline"
        value={headline}
        onChange={setHeadline}
        placeholder="e.g., Senior Investment Advisor"
      />

      <CInput
        label="Bio / Description"
        value={bio}
        onChange={setBio}
        placeholder="Tell users about yourself and your expertise"
        multiline
      />

      {/* SEBI Qualification Dropdown */}
      <div className="mb-3">
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 4 }}>
          SEBI Qualification
        </CText>
        <select
          value={sebiQualification}
          onChange={(e) => setSebiQualification(e.target.value)}
          className="w-full p-3 rounded-xl border text-base"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: colors.borderColor,
            color: colors.textColor,
          }}
        >
          <option value="">Select qualification</option>
          {sebiQualifications.map((qual) => (
            <option key={qual} value={qual}>
              {qual}
            </option>
          ))}
        </select>
      </div>

      <CInput
        label="SEBI Registration Number"
        value={sebiRegNumber}
        onChange={setSebiRegNumber}
        placeholder="INH000XXXXXX"
      />

      <CInput
        label="Years of Experience"
        value={yearsOfExperience}
        onChange={setYearsOfExperience}
        placeholder="e.g., 10"
        type="number"
      />

      <CInput
        label="Languages (comma separated)"
        value={languages}
        onChange={setLanguages}
        placeholder="e.g., English, Hindi, Gujarati"
      />

      <CInput
        label="Call Session Rate (INR)"
        value={callRate}
        onChange={setCallRate}
        placeholder="e.g., 500"
        type="number"
      />

      <CInput
        label="Chat Session Rate (INR)"
        value={chatRate}
        onChange={setChatRate}
        placeholder="e.g., 300"
        type="number"
      />

      {saveError && (
        <CText type="r12" color="#ef4444" style={{ marginTop: '8px' }}>
          {saveError}
        </CText>
      )}

      <div className="mt-6">
        <CButton
          title={loading ? 'Saving...' : 'Complete Setup'}
          onPress={handleSubmit}
          disabled={loading}
        />
      </div>
    </>
  );

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <CHeader
        title={step === 0 ? 'Complete Profile' : step === 1 ? 'Choose Your Path' : 'Expert Details'}
        onPressBack={step > 0 ? handleBack : undefined}
      />

      <div className="max-w-md md:max-w-lg lg:max-w-xl mx-auto px-5 py-4">
        <StepIndicator />

        {step === 0 && renderBasicInfo()}
        {step === 1 && renderPersonaSelection()}
        {step === 2 && renderExpertDetails()}

        <div className="text-center mt-4">
          <span
            className="text-sm cursor-pointer"
            style={{ color: colors.grayScale500 }}
            onClick={() => navigate(routes.home)}
          >
            Skip for now
          </span>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
